"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";

import {
  captureMetaPixelCustom,
  captureMetaPixelPageView,
  captureMetaPixelStandard,
  ensureMetaPixel,
} from "@/lib/analytics/meta-pixel";
import {
  META_EVENTS,
  META_SIGNUP_COMPLETE_COOKIE,
  type MetaSignupCookiePayload,
} from "@/lib/analytics/meta-events";

function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  const found = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  if (!found) return null;
  return decodeURIComponent(found.slice(prefix.length));
}

function deleteCookie(name: string) {
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
}

export function MetaPixelLoader() {
  const pathname = usePathname();

  useEffect(() => {
    captureMetaPixelPageView(pathname ?? "/");
  }, [pathname]);

  useEffect(() => {
    if (!ensureMetaPixel()) return;

    const signupMarker = readCookie(META_SIGNUP_COMPLETE_COOKIE);
    if (signupMarker) {
      let payload: MetaSignupCookiePayload | null = null;
      try {
        payload = JSON.parse(signupMarker) as MetaSignupCookiePayload;
      } catch {
        payload =
          signupMarker === "1"
            ? {}
            : {
                signUp: {
                  eventName: META_EVENTS.SIGN_UP,
                  eventId: signupMarker,
                },
              };
      }

      const signUp = payload?.signUp;
      const completeRegistration = payload?.completeRegistration;
      captureMetaPixelCustom(
        signUp?.eventName ?? META_EVENTS.SIGN_UP,
        { signup_method: "magic_link" },
        signUp?.eventId ? { eventId: signUp.eventId } : undefined,
      );
      captureMetaPixelStandard(
        completeRegistration?.eventName ?? META_EVENTS.COMPLETE_REGISTRATION,
        {
          content_name: "SignUp",
          status: true,
          signup_method: "magic_link",
        },
        completeRegistration?.eventId
          ? { eventId: completeRegistration.eventId }
          : undefined,
      );
      deleteCookie(META_SIGNUP_COMPLETE_COOKIE);
    }
  }, []);

  return null;
}
